import { useTranslations } from "use-intl";

import { CatalogFaqSection, type CatalogFaqItem } from "@/components/catalog-faq-section";
import type { CatalogPricingFaqModel } from "@/components/catalog-pricing-faq-model";
import { AddToListsButton } from "@/components/lists/add-to-lists-button";
import type { DealOfferSummary } from "@/components/marketplace/deal-offers-types";
import type { AddToListsTarget } from "@/lib/add-to-lists-target";

interface PricingFaqInput {
  artist: string;
  entityType: "release" | "work";
  model: CatalogPricingFaqModel;
  releaseId?: string;
  summary: DealOfferSummary;
  title: string;
  workId: string;
}

function pricingFaqAddToListsTarget(input: PricingFaqInput): AddToListsTarget | null {
  if (input.entityType === "work") return { entityType: "work", workId: input.workId };
  if (!input.releaseId) return null;
  return {
    entityType: "release",
    releaseId: input.releaseId,
    workId: input.workId,
  };
}

function useAssessmentFaq(input: PricingFaqInput): CatalogFaqItem {
  const t = useTranslations("release.faq.pricing");
  const addToListsTarget = pricingFaqAddToListsTarget(input);
  const { artist, model, summary, title } = input;
  return {
    id: "price-assessment",
    question: t("goodDeal.question", { artist, title }),
    answer: (
      <div className="space-y-3">
        <p>
          {t("goodDeal.answer", {
            artist,
            assessment: model.assessment.assessment,
            comparisonKind: model.comparisonKind,
            comparisonPrice: model.comparisonPrice,
            currentPrice: model.currentPrice,
            dropPercent: model.dropPercent,
            minimumPrice: model.minimumPrice ?? model.currentPrice,
            provider: summary.providerDisplayName,
            title,
          })}
        </p>
        {model.shouldShowWishlist && addToListsTarget && (
          <AddToListsButton target={addToListsTarget} variant="button" />
        )}
      </div>
    ),
  };
}

function useRangeFaq(input: PricingFaqInput): CatalogFaqItem | null {
  const t = useTranslations("release.faq.pricing");
  const { artist, model, summary, title } = input;
  if (input.entityType !== "work") return null;
  if (summary.offerCount <= 1 || !model.rangeMinimum || !model.rangeMaximum) return null;
  if (Number(summary.allOffersTotalPriceMin) === Number(summary.allOffersTotalPriceMax))
    return null;
  return {
    id: "current-range",
    question: t("range.question", { artist, title }),
    answer: t("range.answer", {
      count: summary.offerCount,
      maximumPrice: model.rangeMaximum,
      minimumPrice: model.rangeMinimum,
      title,
    }),
  };
}

function useOfferCountFaq(input: PricingFaqInput): CatalogFaqItem | null {
  const t = useTranslations("release.faq.pricing");
  if (input.summary.offerCount <= 0) return null;
  return {
    id: "offer-count",
    question: t("offerCount.question", { artist: input.artist, title: input.title }),
    answer: t("offerCount.answer", { count: input.summary.offerCount, title: input.title }),
  };
}

function useHistoricalFaq(input: PricingFaqInput, kind: "low" | "high"): CatalogFaqItem | null {
  const t = useTranslations("release.faq.pricing");
  const price = kind === "low" ? input.model.minimumPrice : input.model.maximumPrice;
  if (input.summary.priceHistoryObservationCount < 2 || !price) return null;
  const historyValue =
    kind === "low" ? input.summary.priceHistoryMin : input.summary.priceHistoryMax;
  const commonValues = {
    isCurrent: Number(input.summary.bestOfferPriceTotal) === Number(historyValue) ? "yes" : "no",
    provider: input.summary.providerDisplayName,
    title: input.title,
  };
  if (kind === "low") {
    return {
      id: "historical-low",
      question: t("historicalLow.question", { artist: input.artist, title: input.title }),
      answer: t("historicalLow.answer", { ...commonValues, minimumPrice: price }),
    };
  }
  return {
    id: "historical-high",
    question: t("historicalHigh.question", { artist: input.artist, title: input.title }),
    answer: t("historicalHigh.answer", {
      ...commonValues,
      maximumPrice: price,
    }),
  };
}

function useObservationsFaq(input: PricingFaqInput): CatalogFaqItem | null {
  const t = useTranslations("release.faq.pricing");
  if (input.summary.priceHistoryObservationCount <= 0) return null;
  return {
    id: "price-observations",
    question: t("observations.question", { artist: input.artist, title: input.title }),
    answer: t("observations.answer", {
      count: input.summary.priceHistoryObservationCount,
      lastCheckedDate: input.model.lastCheckedDate ?? "",
      recency: input.model.lastCheckedDate ? "withDate" : "withoutDate",
      title: input.title,
    }),
  };
}

function presentFaqItems(items: Array<CatalogFaqItem | null>): CatalogFaqItem[] {
  return items.filter((item): item is CatalogFaqItem => item !== null);
}

function useCatalogPricingFaqItems(input: PricingFaqInput): CatalogFaqItem[] {
  return presentFaqItems([
    useAssessmentFaq(input),
    useRangeFaq(input),
    useOfferCountFaq(input),
    useHistoricalFaq(input, "low"),
    useHistoricalFaq(input, "high"),
    useObservationsFaq(input),
  ]);
}

export function CatalogPricingFaqContent({ input }: { input: PricingFaqInput }) {
  const t = useTranslations("release.faq.pricing");
  const items = useCatalogPricingFaqItems(input);
  return <CatalogFaqSection items={items.slice(0, 5)} title={t("title")} />;
}
